import { getOperationName } from '@apollo/client/utilities'
import { useToggle } from 'ahooks'
import { Button, Form, Input, Modal, TreeSelect, message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { DataNode } from 'antd/lib/tree'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { FileUploadButton } from '@/components/file-upload-button/file-upload-button.component'
import { MultipleUserSearch } from '@/components/multiple-user-search/multiple-user-search.component'
import {
  GetKnowledgeGroupPostsDocument,
  RequestedKnowledgePostFragment,
  useGetKnowledgeGroupsQuery,
  useKnowledgePostCreateMutation,
  useKnowledgePostUpdateMutation,
} from '@/generated/graphql'

const KnowledgeCreateEditForm: React.FC<{
  knowledgeGroupId: number
  form: FormInstance<any>
  onClose: () => void
  visible: boolean
  knowledgePost?: RequestedKnowledgePostFragment
}> = ({ knowledgeGroupId, form, onClose, visible, knowledgePost }) => {
  const { t } = useTranslation()

  const { data } = useGetKnowledgeGroupsQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        last: 2000, // 一次获取所有信息
      },
    },
  })

  const nodes = (data?.knowledge.groups.edges.map(({ node }) => node) || []).map((data) => ({
    title: data.name,
    value: data.knowledgeGroupId,
    key: data.knowledgeGroupId,
    parentKey: data.parentKnowledgeGroupId,
  })) as (DataNode & { key: number; parentKey?: number })[]

  const roots = nodes.filter((node) => !node.parentKey)
  nodes.forEach((node) => {
    node.children = nodes.filter((n) => n.parentKey === node.key)
  })

  const [fileIds, setFileIds] = React.useState<number[]>((knowledgePost?.files || []).map((file) => file.fileId))

  const refetchQueries = {
    refetchQueries: [getOperationName(GetKnowledgeGroupPostsDocument)!],
  }

  const [knowledgePostCreate] = useKnowledgePostCreateMutation({
    ...refetchQueries,
  })

  const [knowledgePostUpdate] = useKnowledgePostUpdateMutation({
    ...refetchQueries,
  })

  React.useEffect(() => {
    form.resetFields()
  }, [])

  const onOK = async () => {
    const values = await form.validateFields()

    if (!values.url) {
      if (fileIds.length === 0) {
        message.error(t('knowledge.createKnowledgeError'))
        return
      }
    } else {
      try {
        new URL(values.url)
      } catch (e) {
        message.error(t('knowledge.invalidUrl'))
        return
      }
    }

    const params = {
      title: values.title,
      description: values.description || '',
      ...(values.url && { url: values.url }),
      ...(fileIds.length > 0 && { fileIds }),
      authorId: values.author[0].userId,
    }

    if (knowledgePost) {
      knowledgePostUpdate({
        variables: {
          knowledgePostId: knowledgePost.knowledgePostId,
          knowledgePostInput: {
            knowledgeGroupId: values.catalog,
            ...params,
          },
        },
      })
    } else {
      knowledgePostCreate({
        variables: {
          knowledgePostInput: {
            knowledgeGroupId,
            ...params,
          },
        },
      })
    }
    onClose()
  }

  const initialValues = knowledgePost
    ? {
        title: knowledgePost.title,
        description: knowledgePost.description,
        url: knowledgePost.link?.url || '',
        catalog: knowledgePost.knowledgeGroupId,
        author: [knowledgePost.author],
      }
    : {
        title: '',
        description: '',
        url: '',
        author: [],
      }

  const footer = (
    <div style={{ width: '100%', padding: '16px 0', display: 'flex', justifyContent: 'flex-end' }}>
      <Button onClick={() => onClose()}>{t('post.cancel')}</Button>
      <Button type='primary' style={{ marginLeft: 16 }} onClick={onOK}>
        {knowledgePost ? t('post.save') : t('post.editor.publish')}
      </Button>
    </div>
  )

  return (
    <>
      <Form
        form={form}
        labelCol={{ span: 3 }}
        wrapperCol={{ offset: 1 }}
        validateMessages={{
          required: t('group.createValidation'),
        }}
        initialValues={initialValues}
      >
        <Form.Item label={t('knowledge.title')} name='title' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t('knowledge.intro')} name='description'>
          <Input.TextArea showCount />
        </Form.Item>
        <Form.Item label={t('knowledge.url')} name='url'>
          <Input type='url' />
        </Form.Item>
        {knowledgePost && (
          <Form.Item label={t('knowledge.catalog')} name='catalog'>
            <TreeSelect treeData={roots} />
          </Form.Item>
        )}
        <Form.Item label={t('knowledge.author')} name='author' rules={[{ required: true }]}>
          <MultipleUserSearch singleMode={true} />
        </Form.Item>
      </Form>
      <div style={{ padding: '16px 0' }}>
        <FileUploadButton
          currentFiles={knowledgePost?.files}
          isDone={!visible}
          name='knowledgeFileUpload'
          setFileIds={setFileIds}
        />
      </div>
      {footer}
    </>
  )
}

export const KnowledgeCreateEditModal: React.FC<{
  knowledgeGroupId: number
  knowledgePost?: RequestedKnowledgePostFragment
}> = ({ children, knowledgeGroupId, knowledgePost, ...props }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [visible, { toggle }] = useToggle()

  children = React.cloneElement(children as any, {
    ...props,
    onClick: () => toggle(),
  })

  return (
    <>
      {children}
      <Modal
        title={knowledgePost ? t('knowledge.edit') : t('knowledge.create')}
        visible={visible}
        footer={null}
        closable={false}
        maskClosable={false}
        destroyOnClose
      >
        <KnowledgeCreateEditForm
          knowledgeGroupId={knowledgeGroupId}
          onClose={toggle}
          form={form}
          visible={visible}
          knowledgePost={knowledgePost}
        />
      </Modal>
    </>
  )
}
